import { GISymptoms } from './types/gi-symptoms';

export enum MorphineRecommendation {
  Notstarted,
  Start,
  Increase,
  Nochange,
  Decrease,
  Discontinue,
  Discharge,
}

export type MorphineRecommendationResult = {
  calculatedDose: number;
  recommendation: string;
  recommendationCoded: MorphineRecommendation;
  forceClonidineStart: boolean;
  note: string;
};

export type GetMorphineRecommendationRequest = {
  priorEvaluationScore: number;
  currentEvaluationScore: number;
  priorMorphineRecommendation: MorphineRecommendation;
  priorAssessmentGISymptoms: GISymptoms;
  currentAssessmentGISymptoms: GISymptoms;
  morphineDose: number;
};

const CHANGE_PERCENTAGE_DISPLAY = '10';
const CHANGE_PERCENTAGE = 0.1;
const DISCONTINUE_DETERMINATION_MAX_DOSAGE = 0.0104444444; // Rounded MAX

const shouldStartClonidine = (priorScore: number, currentScore: number, hasConsistentPoorStooling: boolean) => {
  return (
    (hasConsistentPoorStooling && priorScore === 3 && currentScore === 3) || (priorScore === 4 && currentScore === 4)
  );
};

const shouldIncreaseDose = (priorScore: number, currentScore: number) => {
  return (priorScore >= 3 && currentScore === 6) || (priorScore === 5 && currentScore === 5);
};

const shouldDecreaseDose = (priorScore: number, currentScore: number) => {
  return priorScore <= 3 && currentScore < 3;
};

const handleNotStarted = (
  priorScore: number,
  currentScore: number,
  hasConsistentPoorStooling: boolean,
): MorphineRecommendationResult => {
  if (shouldStartClonidine(priorScore, currentScore, hasConsistentPoorStooling)) {
    return {
      calculatedDose: 0,
      recommendation: 'No Medication',
      recommendationCoded: MorphineRecommendation.Notstarted,
      forceClonidineStart: true,
      note: 'Prior: NotStarted -> Force Clonidine to start, but not Morphine',
    };
  } else if (priorScore >= 3 && currentScore >= 5) {
    return {
      calculatedDose: 0.05,
      recommendation: 'Start 0.05 mg/kg q3',
      recommendationCoded: MorphineRecommendation.Start,
      forceClonidineStart: false,
      note: 'Prior: NotStarted -> Start Morphine',
    };
  } else {
    return {
      calculatedDose: 0,
      recommendation: 'No Medication',
      recommendationCoded: MorphineRecommendation.Notstarted,
      forceClonidineStart: false,
      note: "Prior: NotStarted -> Don't start",
    };
  }
};

const handleStartedOrIncreased = (
  priorScore: number,
  currentScore: number,
  _hasConsistentPoorStooling: boolean,
  morphineDose: number,
): MorphineRecommendationResult => {
  if (shouldIncreaseDose(priorScore, currentScore)) {
    return {
      calculatedDose: fixFloatPrecision(morphineDose + morphineDose * CHANGE_PERCENTAGE),
      recommendation: `+${CHANGE_PERCENTAGE_DISPLAY}% mg/kg`,
      recommendationCoded: MorphineRecommendation.Increase,
      forceClonidineStart: false,
      note: 'Prior: StartedOrIncreased -> Increase',
    };
  } else {
    return {
      calculatedDose: morphineDose,
      recommendation: 'No Change',
      recommendationCoded: MorphineRecommendation.Nochange,
      forceClonidineStart: false,
      note: 'Prior: StartedOrIncreased -> No Change',
    };
  }
};

const handleUnchangedOrWeanedOrDiscontinued = (
  priorScore: number,
  currentScore: number,
  _hasConsistentPoorStooling: boolean,
  morphineDose: number,
): MorphineRecommendationResult => {
  if (shouldDecreaseDose(priorScore, currentScore)) {
    if (morphineDose > DISCONTINUE_DETERMINATION_MAX_DOSAGE) {
      return {
        calculatedDose: fixFloatPrecision(morphineDose - morphineDose * CHANGE_PERCENTAGE),
        recommendation: `-${CHANGE_PERCENTAGE_DISPLAY}% mg/kg`,
        recommendationCoded: MorphineRecommendation.Decrease,
        forceClonidineStart: false,
        note: 'Prior: UnchangedOrWeanedOrDiscontinued -> Decrease',
      };
    } else {
      return {
        calculatedDose: 0,
        recommendation: 'Discontinue',
        recommendationCoded: MorphineRecommendation.Discontinue,
        forceClonidineStart: false,
        note: 'Prior: UnchangedOrWeanedOrDiscontinued -> Discontinue',
      };
    }
  } else if ((priorScore === 3 && currentScore === 3) || currentScore >= 5) {
    return {
      calculatedDose: fixFloatPrecision(morphineDose + morphineDose * CHANGE_PERCENTAGE),
      recommendation: `+${CHANGE_PERCENTAGE_DISPLAY}% mg/kg`,
      recommendationCoded: MorphineRecommendation.Increase,
      forceClonidineStart: false,
      note: 'Prior: UnchangedOrWeanedOrDiscontinued -> Increase',
    };
  } else {
    return {
      calculatedDose: morphineDose,
      recommendation: 'No Change',
      recommendationCoded: MorphineRecommendation.Nochange,
      forceClonidineStart: false,
      note: 'Prior: UnchangedOrWeanedOrDiscontinued -> No Change',
    };
  }
};

const handleDefault = (
  _priorScore: number,
  _currentScore: number,
  _hasConsistentPoorStooling: boolean,
  morphineDose: number,
): MorphineRecommendationResult => {
  return {
    calculatedDose: morphineDose,
    recommendation: 'No Change',
    recommendationCoded: MorphineRecommendation.Nochange,
    forceClonidineStart: false,
    note: 'Default -> No Change',
  };
};

const handlers = {
  [MorphineRecommendation.Notstarted]: handleNotStarted,
  [MorphineRecommendation.Start]: handleStartedOrIncreased,
  [MorphineRecommendation.Increase]: handleStartedOrIncreased,
  [MorphineRecommendation.Nochange]: handleUnchangedOrWeanedOrDiscontinued,
  [MorphineRecommendation.Decrease]: handleUnchangedOrWeanedOrDiscontinued,
  [MorphineRecommendation.Discontinue]: handleUnchangedOrWeanedOrDiscontinued,
  [MorphineRecommendation.Discharge]: handleUnchangedOrWeanedOrDiscontinued,
  default: handleDefault,
};

const fixFloatPrecision = (value: number): number => {
  return parseFloat(value.toPrecision(12));
};

export const getDosageAndClonidineRecommendation = (
  request: GetMorphineRecommendationRequest,
): MorphineRecommendationResult => {
  const fixedMorphineDose = fixFloatPrecision(request.morphineDose);
  const hasConsistentPoorStooling =
    request.priorAssessmentGISymptoms.poorStooling && request.currentAssessmentGISymptoms.poorStooling;
  
  // If morphine dose is 0, treat as not started regardless of prior recommendation
  // This prevents trying to calculate percentage increases from 0
  if (fixedMorphineDose === 0) {
    return handleNotStarted(
      request.priorEvaluationScore,
      request.currentEvaluationScore,
      hasConsistentPoorStooling,
    );
  }
  
  const handler = handlers[request.priorMorphineRecommendation] || handlers['default'];
  return handler(
    request.priorEvaluationScore,
    request.currentEvaluationScore,
    hasConsistentPoorStooling,
    fixedMorphineDose,
  );
};
